/**
 * CrosswalkContent - Content renderer for Standards Crosswalk panel
 * 
 * Displays standards mappings between documents and regulatory requirements.
 * Supports filtering by standard and clause.
 */

import { useState } from 'react';
import { Button, Input, Label, RadixSelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';

interface CrosswalkEntry {
  path: string;
  standards?: Record<string, string[]>;
}

interface Standard {
  code: string;
  name: string;
  description: string;
}

interface CrosswalkContentProps {
  entries: CrosswalkEntry[];
  standards: Standard[];
  loading: boolean;
  isSearching: boolean;
  searchQuery: string;
  filterStandard: string;
  filterClause: string;
  onFilterChange: (standard: string, clause: string) => void;
  onExportCSV: () => void;
}

export function CrosswalkContent({
  entries,
  standards,
  loading,
  isSearching,
  searchQuery,
  filterStandard,
  filterClause,
  onFilterChange,
  onExportCSV,
}: CrosswalkContentProps) {
  // Use "all" for Select value since Radix doesn't allow empty string
  const [localStandard, setLocalStandard] = useState(filterStandard || 'all');
  const [localClause, setLocalClause] = useState(filterClause);
  
  // Loading state
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3 mx-auto"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Searching state
  if (isSearching) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin text-3xl mb-3">🔍</div>
        <p className="text-muted-foreground">Searching crosswalk...</p>
      </div>
    );
  }
  
  // Filter entries by search query
  const filteredEntries = searchQuery.trim()
    ? entries.filter(e => e.path.toLowerCase().includes(searchQuery.toLowerCase()))
    : entries;
  
  // Calculate stats
  const totalMappings = entries.reduce((sum, e) => 
    sum + (e.standards ? Object.keys(e.standards).length : 0), 0
  );
  
  // Empty state
  if (entries.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">🔀</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          No crosswalk data
        </h3>
        <p className="text-muted-foreground">
          Standards crosswalk mappings will appear here
        </p>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full">
      {/* Stats bar */}
      <div className="flex-shrink-0 px-4 py-2 bg-muted/30">
        <div className="flex items-center gap-4 text-xs">
          <span className="text-muted-foreground">
            <strong className="text-foreground">{entries.length}</strong> Documents
          </span>
          <span className="text-muted-foreground">
            <strong className="text-info">{standards.length}</strong> Standards
          </span>
          <span className="text-muted-foreground">
            <strong className="text-success">{totalMappings}</strong> Mappings
          </span>
          <Button
            variant="success"
            size="xs"
            onClick={onExportCSV}
            className="ml-auto"
          >
            Export CSV
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex-shrink-0 px-4 py-3">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="block text-xs text-muted-foreground mb-1">
              Standard
            </Label>
            <RadixSelect value={localStandard} onValueChange={setLocalStandard}>
              <SelectTrigger className="w-full text-sm">
                <SelectValue placeholder="All Standards" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Standards</SelectItem>
                {standards.map(std => (
                  <SelectItem key={std.code} value={std.code || `std-${std.name}`}>
                    {std.code} - {std.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </RadixSelect>
          </div>
          <div className="flex-1">
            <Label className="block text-xs text-muted-foreground mb-1">
              Clause
            </Label>
            <Input
              type="text"
              value={localClause}
              onChange={(e) => setLocalClause(e.target.value)}
              placeholder="e.g., 11.1"
              className="w-full text-sm"
            />
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => onFilterChange(localStandard === 'all' ? '' : localStandard, localClause)}
          >
            Apply
          </Button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {filteredEntries.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-3">📭</div>
            <p className="text-muted-foreground">
              {searchQuery.trim() 
                ? `No entries match "${searchQuery}"`
                : 'No entries match the current filters'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {filteredEntries.map((entry, idx) => (
              <CrosswalkRow key={idx} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Crosswalk row component
interface CrosswalkRowProps {
  entry: CrosswalkEntry;
}

function CrosswalkRow({ entry }: CrosswalkRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const standardsList = entry.standards ? Object.keys(entry.standards) : [];
  const totalClauses = entry.standards 
    ? Object.values(entry.standards).reduce((sum, clauses) => sum + clauses.length, 0)
    : 0;
  
  return (
    <div className="px-4 py-3">
      {/* Header */}
      <div 
        className="flex items-start justify-between gap-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-info truncate hover:underline">
            {entry.path}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Standards badges */}
          <div className="flex flex-wrap gap-1">
            {standardsList.slice(0, 3).map(std => (
              <span
                key={std}
                className="text-xs px-1.5 py-0.5 bg-info-light text-info rounded"
              >
                {std.toUpperCase()}
              </span>
            ))}
            {standardsList.length > 3 && (
              <span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground rounded">
                +{standardsList.length - 3}
              </span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {totalClauses} clause{totalClauses !== 1 ? 's' : ''}
          </span>
          <span className="text-muted-foreground text-xs">
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
      </div>
      
      {/* Expanded details */}
      {isExpanded && entry.standards && (
        <div className="mt-3 pl-4 border-l-2 border-border space-y-2">
          {Object.entries(entry.standards).map(([std, clauses]) => (
            <div key={std} className="text-sm">
              <span className="font-medium text-foreground">
                {std.toUpperCase()}:
              </span>
              <span className="ml-2 text-muted-foreground">
                {(clauses as string[]).join(', ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
